import { ChevronLeft, ChevronRight, RotateCw, ShieldCheck, DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToolTip } from "@/hooks/useToolTip";
import { fileDb } from "@/db/fileDatabase";
import { usePreviewBrowser } from "./usePreviewBrowser";

/**
 * Toolbar for the preview browser tab: back/forward/reload buttons plus a
 * read-only URL bar showing the current path.
 */
export function PreviewBrowserToolbar({ workspaceId }: { workspaceId: string }) {
  const { currentPath, canGoBack, canGoForward, goBack, goForward, reload } = usePreviewBrowser(workspaceId);

  const sandboxInfo = useToolTip<HTMLSpanElement>({
    text: "This preview runs in a sandboxed environment.\n\nOnly workspace assets can be viewed — external links are blocked for security.",
    placement: "bottom-end",
  });

  const handleDownload = async () => {
    if (!currentPath) return;
    // currentPath is a URL path (e.g. "/docs/index.html") — reconstruct the DB path
    const filePath = `${workspaceId}${currentPath}`;
    const file = await fileDb.files.where("filePath").equals(filePath).first();
    if (!file) return;
    const link = document.createElement("a");
    link.href = `/api/file/${file.id}`;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-200 dark:border-zinc-800">
      <Button
        intent="none"
        icon={<ChevronLeft className="w-4 h-4" />}
        toolTip="Back"
        disabled={!canGoBack}
        onClick={goBack}
        className="p-1"
      />
      <Button
        intent="none"
        icon={<ChevronRight className="w-4 h-4" />}
        toolTip="Forward"
        disabled={!canGoForward}
        onClick={goForward}
        className="p-1"
      />
      <Button
        intent="none"
        icon={<RotateCw className="w-4 h-4" />}
        toolTip="Reload"
        onClick={reload}
        className="p-1"
      />
      <input
        className="flex-1 px-2 py-1 text-xs bg-gray-100 dark:bg-zinc-900 border border-gray-300 dark:border-zinc-800 rounded text-gray-600 dark:text-zinc-400 select-all"
        value={currentPath}
        readOnly
        title={currentPath}
        aria-label="Current path"
      />
      <Button
        intent="none"
        icon={<DownloadIcon className="w-4 h-4" />}
        toolTip="Download"
        disabled={!currentPath}
        onClick={handleDownload}
        className="p-1"
      />
      <span
        ref={sandboxInfo.ref}
        {...sandboxInfo.hoverProps}
        className="p-1 text-gray-400 dark:text-zinc-500 cursor-default"
      >
        <ShieldCheck className="w-4 h-4" />
      </span>
      {sandboxInfo.tooltip}
    </div>
  );
}
